import React, { useEffect, useMemo, useState } from "react"
import { Button, Col, Container, Nav, NavItem, NavLink, Row } from "reactstrap"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import Spinners from "components/Shared/Spinners"
import { getCardBatches, getIdCardsList } from "store/actions/cardsAction"
import {
  resetPayload,
  setPayload,
  toggleActiveTab,
  toggleFilterVisibility,
} from "store/reducers/cardReducer"
import { usePermissions } from "hooks"
import UpdateAssetsStatusByFileModal from "./component/UpdateAssetsStatusByFileModal"
import BatchTable from "./component/BatchTable"
import IDCardsTable from "./component/IDCardsTable"
import GenerateIDCardsModal from "./component/GenerateIDCardsModal"
import Filters from "../../components/Shared/Filters"
import { getJobTitlesList } from "../../store/actions/commonAction"
import { Filter, FilterX } from "lucide-react"

const navItems = [
  {
    name: "Groups",
    id: "groups",
  },
  {
    name: "Generated",
    id: "generated",
  },
  {
    name: "Printed",
    id: "printed",
  },
  {
    name: "Dispatched",
    id: "dispatched",
  },
  {
    name: "Confirmed",
    id: "confirmed",
  },
]

const IdCards = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation()
  const dispatch = useDispatch()
  const {
    loading,
    isLoading,
    totalPages,
    totalCount,
    currentPage,
    payload,
    batches,
    activeTab,
    candidates,
    isShowFilter,
  } = useSelector(state => state.Card)
  const { jobTitlesList } = useSelector(state => state.Common)
  const { permissions, showAll } = usePermissions("id_card")
  const [trigger, setTrigger] = useState(false)
  const [updateAllStatusModal, setUpdateAllStatusModal] = useState(false)
  const [showGenerateIdCardModal, setShowGenerateIdCardModal] = useState(false)

  useEffect(() => {
    dispatch(getJobTitlesList())
  }, [])

  useEffect(() => {
    if (activeTab === "groups") {
      getBatches()
    } else {
      getIdCradList()
    }
  }, [trigger, activeTab])

  const getBatches = () => {
    dispatch(getCardBatches(payload))
  }

  const getIdCradList = () => {
    const data = {
      ...payload,
      filters: {
        ...payload.filters,
        type:
          activeTab === "generated"
            ? 0
            : activeTab === "printed"
            ? 1
            : activeTab === "dispatched"
            ? 2
            : activeTab === "confirmed"
            ? 3
            : 0,
      },
    }
    dispatch(getIdCardsList(data))
  }

  const toggleTab = tab => {
    if (activeTab !== tab) {
      dispatch(toggleActiveTab(tab))
      dispatch(resetPayload())
    }
  }

  const onPageChange = page => {
    dispatch(setPayload({ page_number: page }))
    setTrigger(!trigger)
  }

  const handleReset = () => {
    dispatch(resetPayload())
    setTrigger(!trigger)
  }

  const getFilterState = () => {
    return {
      ...payload.filters,
      order_by: payload.order_by,
    }
  }

  const handleFilterDataChange = data => {
    const { order_by, ...rest } = data
    dispatch(
      setPayload({
        order_by: Number(order_by),
        filters: { ...payload.filters, ...rest },
      })
    )
  }

  const formattedJobTitles = useMemo(() => {
    if (!jobTitlesList) return []

    return jobTitlesList.map(jobTitle => ({
      value: jobTitle.id,
      label: jobTitle[language === "en" ? "name" : "name_ar"],
    }))
  }, [jobTitlesList])

  const filters = {
    keyword: {
      type: "text",
      placeholder:
        activeTab === "groups"
          ? t("Search by batch serial number")
          : t("Search by name,national id or phone number"),
      label: t("Search By"),
      colSize: 4,
    },

    job_title_ids: {
      type: "select",
      placeholder: t("Select Job Title"),
      label: t("Job Title"),
      colSize: 4,
      options: formattedJobTitles,
      multiple: true,
    },
    order_by: {
      type: "select",
      placeholder: t("Select Order"),
      label: t("Select Order"),
      options: [
        { value: 1, label: t("Ascending") },
        { value: -1, label: t("Descending") },
      ],
    },
  }

  if (activeTab === "groups") {
    delete filters.job_title_ids
  }

  if (loading) return <Spinners />

  return (
    <>
      <div className="page-content">
        <Container fluid>
          <Row>
            <Col xs={12}>
              <div className="d-flex mb-4 justify-content-between align-items-center">
                <h4 className="card-title">{t("ID Cards")}</h4>
                <div className="d-flex gap-3 align-items-center justify-content-center">
                  {(showAll || permissions.includes("can_generate")) && (
                    <div>
                      <button
                        className="main_btn"
                        onClick={() => {
                          setShowGenerateIdCardModal(true)
                        }}
                      >
                        {t("Generate Id Cards")}
                      </button>
                    </div>
                  )}
                  {(showAll || permissions.includes("can_generate")) && (
                    <div>
                      <button
                        className="main_btn"
                        onClick={() => {
                          setUpdateAllStatusModal(true)
                        }}
                      >
                        {t("Update Assets Status")}
                      </button>
                    </div>
                  )}
                  <Button
                    color="primary"
                    onClick={() => dispatch(toggleFilterVisibility())}
                    title={t("Show/Hide Filter")}
                  >
                    {isShowFilter ? <FilterX /> : <Filter />}
                  </Button>
                </div>
              </div>
            </Col>
            <Col xs="12">
              <Nav tabs className="one_line_parent mb-3">
                {navItems.map(nav => (
                  <NavItem key={nav.id}>
                    <NavLink
                      className={activeTab === nav.id ? "active" : ""}
                      onClick={() => toggleTab(nav.id)}
                    >
                      {t(nav.name)}
                    </NavLink>
                  </NavItem>
                ))}
              </Nav>
              {isShowFilter && (
                <Filters
                  filters={filters}
                  state={getFilterState()}
                  setState={handleFilterDataChange}
                  handleSearch={() => setTrigger(!trigger)}
                  handleReset={handleReset}
                />
              )}
            </Col>
            <Col xs="12">
              {activeTab === "groups" ? (
                <BatchTable
                  data={batches}
                  currentPage={currentPage}
                  loading={loading}
                  onPageChange={onPageChange}
                  totalCount={totalCount}
                  totalPages={totalPages}
                  trigger={() => setTrigger(!trigger)}
                  payload={payload}
                />
              ) : (
                <IDCardsTable
                  data={candidates}
                  currentPage={currentPage}
                  loading={loading}
                  onPageChange={onPageChange}
                  totalCount={totalCount}
                  totalPages={totalPages}
                  trigger={() => setTrigger(!trigger)}
                  payload={payload}
                  activeTab={activeTab}
                />
              )}
            </Col>
          </Row>
        </Container>
      </div>
      {showGenerateIdCardModal && (
        <GenerateIDCardsModal
          isOpen={showGenerateIdCardModal}
          toggle={() => setShowGenerateIdCardModal(false)}
          trigger={() => setTrigger(!trigger)}
        />
      )}
      {updateAllStatusModal && (
        <UpdateAssetsStatusByFileModal
          isOpen={updateAllStatusModal}
          toggle={() => setUpdateAllStatusModal(false)}
          trigger={() => setTrigger(!trigger)}
        />
      )}
    </>
  )
}

export default IdCards
